import {
    Fab,
    Grid,
    IconButton,
    Popper,
    Fade,
    Card,
    CardHeader,
    List,
    Divider,
    Tooltip,
    makeStyles,
} from "@material-ui/core";
import { Close } from "@material-ui/icons";
import LightIcon from "@material-ui/icons/EmojiObjects";
import clsx from "clsx";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { RelayAction } from "@/api/streamMetadata";
import * as FullStory from "@/utilities/hooks/fullstory";
import { OpenButton } from "@/utilities/types/streaming";

import useActionButtonStyles from "../ActionButton.styles";
import LightsButtonItem from "./LightsButtonItem";


const useStyles = makeStyles((theme) => {
    return {
        close: {
            position: "absolute",
            top: 8,
            right: 8,
            color: theme.palette.primary.contrastText,
        },
        divider: {
            backgroundColor: theme.palette.primary.light,
        },
        lightsList: {
            maxHeight: 400,
            overflowX: "auto",
        },
        menu: {
            backgroundColor: theme.palette.primary.dark,
            color: theme.palette.primary.contrastText,
            borderRadius: 8,
        },
        popper: {
            zIndex: theme.zIndex.modal,
            marginBottom: 16,
            width: 300,
            marginRight: "0.625rem",
        },
    };
});

interface LightsButtonProps {
    lights: RelayAction[];
    menuAnchor: HTMLElement | null;
    handleMenu: (button: OpenButton) => void;
    open: boolean;
}

const LightsButton: React.FC<LightsButtonProps> = ({ lights, menuAnchor, handleMenu, open }) => {
    const { t } = useTranslation("translation", { keyPrefix: "components.streamContent" });

    const actionButtonClasses = useActionButtonStyles();
    const styles = useStyles();
    const [showTooltip, setShowTooltip] = useState(false);

    const id = open ? "popper" : undefined;

    const handleClick = () => {
        setShowTooltip(false);
        handleMenu(OpenButton.Lights);
    };

    return (
        <>
            <Popper
                id={id}
                data-testid="lights-button_popper"
                open={open}
                disablePortal
                anchorEl={menuAnchor}
                transition
                placement="top-end"
                className={styles.popper}
            >
                {({ TransitionProps }) => (
                    <Fade {...TransitionProps} timeout={350}>
                        <Card raised className={styles.menu}>
                            <IconButton
                                id={"streaming_lights-button_close-button"}
                                data-testid="lights-button_close-button"
                                aria-label="close"
                                onClick={() => handleMenu(OpenButton.None)}
                                className={styles.close}
                            >
                                <Close />
                            </IconButton>
                            <CardHeader title="Light Options" />
                            <Divider className={styles.divider} />
                            <List classes={{ root: styles.lightsList }}>
                                {lights.map((light) => (
                                    <LightsButtonItem key={light.uiLabel} light={light} />
                                ))}
                            </List>
                        </Card>
                    </Fade>
                )}
            </Popper>
            <Grid item className={actionButtonClasses.fabItem}>
                <Tooltip
                    PopperProps={{ disablePortal: true }}
                    open={showTooltip}
                    disableHoverListener
                    onMouseEnter={() => !open && setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                    title={t("lightsButton.lights")}
                    placement="top"
                >
                    <Fab
                        id={"streaming_lights-button"}
                        className={clsx(
                            actionButtonClasses.fab,
                            open && actionButtonClasses.active,
                            open && FullStory.names.classes.streaming.lightMenu,
                        )}
                        aria-label="lights-button"
                        onClick={handleClick}
                    >
                        <LightIcon />
                    </Fab>
                </Tooltip>
            </Grid>
        </>
    );
};
export default LightsButton;
